<template>
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-[1001] bg-black/30 transition-opacity duration-300"
    :class="isOpen ? (isAnimating ? 'opacity-100' : 'opacity-0') : 'hidden'"
    @click="handleBackdropClick"
  ></div>

  <!-- Modal -->
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="signup-form-title"
    class="pointer-events-none fixed inset-x-0 bottom-3 z-[1002] mx-auto flex justify-end px-6 min-[480px]:bottom-6 sm:right-6 xl:px-0"
    :class="{ hidden: !isOpen }"
  >
    <div class="pointer-events-none flex w-full max-w-7xl justify-end">
      <!-- Card -->
      <div
        class="pointer-events-auto w-full max-w-md transition-all duration-500 ease-out"
        :class="isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'"
      >
        <div class="overflow-hidden rounded-2xl border-2 border-blue-700 bg-blue-50 shadow-xl ring-1 ring-black/5">
          <div class="max-h-[90vh] overflow-y-auto">
            <div class="flex items-start justify-between gap-3 px-4 py-4 sm:gap-4 sm:px-6 sm:py-5">
              <div>
                <h3 id="signup-form-title" class="mb-1 text-base font-bold leading-snug sm:text-lg">
                  {{ subscriber ? 'Update Your Preferences' : 'Stay Updated' }}
                </h3>
                <p class="text-xs sm:text-sm">
                  {{
                    subscriber
                      ? "Add or change what you'd like to hear from us about."
                      : 'Tell us a bit about you so we can send the right updates.'
                  }}
                </p>
              </div>

              <button
                type="button"
                class="shrink-0 rounded-full p-1.5 text-gray-600 transition hover:bg-gray-600/75 hover:text-white"
                aria-label="Close"
                @click="dismiss"
              >
                <svg
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form
              ref="form"
              class="space-y-4 px-4 pb-4 sm:px-6 sm:pb-5"
              @submit.prevent="submit"
              @input="markTouched"
              @change="markTouched"
            >
              <div class="text-sm text-red-500" v-show="errors.general">{{ errors.general }}</div>

              <Textbox
                ref="emailInput"
                name="email"
                label="Email"
                type="email"
                placeholder="you@example.com"
                :error="errors.email"
                v-model="form.email"
                required
                autocomplete="on"
              />

              <fieldset class="m-0 border-0 p-0">
                <legend class="mb-1 block text-xs font-medium leading-6 text-gray-700">
                  What practice type(s) are you interested in? <span class="text-red-600" aria-hidden="true">*</span
                  ><span class="sr-only">(required)</span>
                </legend>

                <div class="mt-2 grid grid-cols-2 gap-3 sm:gap-2">
                  <Checkbox
                    v-for="type in practiceTypes"
                    :key="type.value"
                    :name="'practice_type[]'"
                    :value="type.value"
                    :label="type.label"
                    v-model="form.practiceTypes"
                  />
                </div>

                <div class="mt-1.5 text-xs text-red-500" v-show="errors.practiceTypes">{{ errors.practiceTypes }}</div>
              </fieldset>

              <div>
                <Select
                  label="What state(s) are you interested in?"
                  name="states"
                  :options="states"
                  none-selected-label="Select at least one"
                  v-model:selected="selectedStates"
                  :error="errors.states"
                  multiple
                />
              </div>

              <fieldset class="m-0 border-0 p-0">
                <legend class="mb-1 block text-xs font-medium leading-6 text-gray-700">
                  What type of content would you like to receive? <span class="text-red-600" aria-hidden="true">*</span
                  ><span class="sr-only">(required)</span>
                </legend>

                <div class="mt-2 space-y-3 sm:space-y-2">
                  <Checkbox name="interests[]" value="ask-the-experts" label="Ask the Experts videos" v-model="form.interests" />
                  <Checkbox name="interests[]" value="articles" label="Articles" v-model="form.interests" />
                  <Checkbox name="interests[]" value="events" label="Events, Conferences & Seminars" v-model="form.interests" />
                </div>

                <div class="mt-1.5 text-xs text-red-500" v-show="errors.interests">{{ errors.interests }}</div>
              </fieldset>

              <button type="submit" class="btn-secondary w-full py-2 text-center text-sm">
                {{ subscriber ? 'Update Preferences' : 'Sign Up' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Checkbox from './Checkbox.vue'
import Select from './Select.vue'
import Textbox from './Textbox.vue'

export default {
  components: { Checkbox, Select, Textbox },

  props: {
    practiceTypes: {
      type: Array,
      default: () => [],
    },
    states: {
      type: Array,
      default: () => [],
    },
    subscriber: {
      type: Object,
      default: () => null,
    },
  },

  data() {
    return {
      errors: this.getDefaultErrors(),
      form: this.getDefaultForm(),
      formTouched: false,
      isOpen: false,
      isAnimating: false,
    }
  },

  mounted() {
    this.openHandler = () => this.show()
    document.querySelectorAll('.signup-open-form').forEach((btn) => {
      btn.addEventListener('click', this.openHandler)
    })

    this.popupCard = document.getElementById('signup-popup-card')
    this.popupCloseBtn = document.getElementById('signup-popup-close')
    this.popupOpenBtn = document.getElementById('signup-popup-open-form')

    if (this.popupCard) {
      this.popupTimer = setTimeout(this.showPopup, 2000)
      this.popupCloseBtn?.addEventListener('click', this.dismissPopup)
      this.popupOpenBtn?.addEventListener('click', this.show)
    }
  },

  beforeUnmount() {
    document.querySelectorAll('.signup-open-form').forEach((btn) => {
      btn.removeEventListener('click', this.openHandler)
    })

    clearTimeout(this.popupTimer)
    this.popupCloseBtn?.removeEventListener('click', this.dismissPopup)
    this.popupOpenBtn?.removeEventListener('click', this.show)
  },

  computed: {
    selectedStates: {
      get() {
        return this.form.states.join(',')
      },
      set(newSelectedOptions) {
        this.form.states = (newSelectedOptions || []).map((option) => option.value)
      },
    },
  },

  methods: {
    dismiss() {
      this.isAnimating = false
      document.body.style.overflow = ''

      setTimeout(() => {
        this.isOpen = false
        this.formTouched = false
        this.form = this.getDefaultForm()
        this.errors = this.getDefaultErrors()
      }, 300)
    },

    dismissPopup() {
      this.popupCard?.classList.add('opacity-0', 'translate-y-4')
      setTimeout(() => this.popupCard?.classList.add('hidden'), 300)
    },

    getDefaultErrors() {
      return {
        email: '',
        interests: '',
        practiceTypes: '',
        states: '',
      }
    },

    getDefaultForm() {
      return {
        email: this.subscriber?.email ?? '',
        practiceTypes: this.subscriber?.practiceTypes ?? [],
        states: this.subscriber?.states ?? [],
        interests: this.subscriber?.interests ?? [],
      }
    },

    getRecaptchaToken() {
      return new Promise((resolve, reject) => {
        if (typeof grecaptcha === 'undefined') {
          reject(new Error('reCAPTCHA not loaded'))
          return
        }

        grecaptcha.ready(() => {
          grecaptcha.execute(window.recaptchaSiteKey, { action: 'subscribe' }).then((token) => resolve(token))
        })
      })
    },

    handleBackdropClick() {
      if (!this.formTouched) {
        this.dismiss()
      }
    },

    markTouched() {
      this.formTouched = true
    },

    show() {
      this.dismissPopup()
      this.isOpen = true
      document.body.style.overflow = 'hidden'

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.isAnimating = true
        })
      })

      this.$nextTick(() => {
        this.$refs.emailInput?.focus?.()
      })
    },

    showPopup() {
      this.popupCard?.classList.remove('hidden')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.popupCard?.classList.remove('opacity-0', 'translate-y-4')
        })
      })
    },

    async submit() {
      this.errors = this.getDefaultErrors()

      const token = await this.getRecaptchaToken()

      axios
        .post(
          '/get-notified/subscribe',
          {
            ...this.form,
            recaptcha: token,
            action: 'subscribe',
          },
          {
            headers: { Accept: 'application/json' },
          },
        )
        .then(() => {
          window.location.href = '/get-notified/verification-notice'
        })
        .catch((error) => {
          const data = error.response?.data

          if (error.response?.status === 422 && data?.errors) {
            this.errors.email = data.errors.email?.[0] ?? ''
            this.errors.practiceTypes = data.errors.practiceTypes?.[0] ?? ''
            this.errors.states = data.errors.states?.[0] ?? ''
            this.errors.interests = data.errors.interests?.[0] ?? ''
          } else {
            this.errors.general = data?.message ?? 'Something went wrong. Please try again.'
          }
        })
    },
  },
}
</script>
